import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { BooleanOperandContours } from "@/document/operations/booleanGeometry";
import { refreshBooleanNode, sourceCacheKey } from "@/document/operations/booleanTree";
import { boundsFromContours } from "@/document/operations/pathGeometry";
import { nodeToBooleanOperand } from "@/editor/shell/shellVectorConversion";
import type { EditorCommandHost } from "@/editor/commands/editorCommandHost";

export function documentHasBooleanSource(document: CurvaDocument, nodeId: string): boolean {
  return document.pages.some(page => nodesHaveBooleanSource(page.children, nodeId));
}

function nodesHaveBooleanSource(nodes: SceneNode[], nodeId: string): boolean {
  return nodes.some(node => {
    if (node.type === "boolean" && node.sourceNodes.some(source => nodeContainsId(source, nodeId))) {
      return true;
    }

    const children = getSceneNodeChildren(node);
    return children ? nodesHaveBooleanSource(children, nodeId) : false;
  });
}

function nodeContainsId(node: SceneNode, nodeId: string): boolean {
  if (node.id === nodeId) {
    return true;
  }

  const children = getSceneNodeChildren(node);
  return children ? children.some(child => nodeContainsId(child, nodeId)) : false;
}

export function refreshBooleanOperandGeometryInDocument(document: CurvaDocument, operand: BooleanOperandContours): boolean {
  return document.pages.reduce((changed, page) => refreshBooleanOperandGeometryInNodes(page.children, operand) || changed, false);
}

function refreshBooleanOperandGeometryInNodes(nodes: SceneNode[], operand: BooleanOperandContours): boolean {
  let changed = false;
  for (const node of nodes) {
    const children = getSceneNodeChildren(node);
    const childChanged = children ? refreshBooleanOperandGeometryInNodes(children, operand) : false;
    const directSourceChanged = node.type === "boolean" && node.sourceNodes.some(source => source.id === operand.nodeId);

    if (node.type === "boolean" && directSourceChanged) {
      const nextSource = {
        nodeId: operand.nodeId,
        sourceBounds: operand.sourceBounds,
        sourceKey: operand.sourceKey,
        bounds: boundsFromContours(operand.contours),
        path: operand.path,
        contours: operand.contours.map(contour => contour.map(point => ({ ...point }))),
      };
      const existing = node.sourceGeometry ?? [];
      const sourceIndex = existing.findIndex(source => source.nodeId === operand.nodeId);
      node.sourceGeometry = sourceIndex >= 0
        ? existing.map((source, index) => index === sourceIndex ? nextSource : source)
        : [...existing, nextSource];
    }

    if (node.type === "boolean" && (directSourceChanged || childChanged)) {
      refreshBooleanNode(node);
      changed = true;
      continue;
    }

    changed = childChanged || changed;
  }

  return changed;
}

export function scheduleTextBooleanOperandRefresh(host: EditorCommandHost, baseDocument: CurvaDocument, nodeId: string): void {
  const source = findSceneNode(baseDocument, nodeId);
  if (source?.type !== "text" || !documentHasBooleanSource(baseDocument, nodeId)) {
    return;
  }

  const textKey = sourceCacheKey(source);
  void nodeToBooleanOperand(structuredClone(source)).then(operand => {
    if (operand.contours.length === 0) {
      return;
    }

    host.patchDocument(current => {
      const currentSource = findSceneNode(current, nodeId);
      if (currentSource?.type !== "text" || sourceCacheKey(currentSource) !== textKey) {
        return current;
      }

      const next = structuredClone(current);
      const changed = refreshBooleanOperandGeometryInDocument(next, operand);
      if (!changed) {
        return current;
      }

      return {
        ...next,
        revision: current.revision + 1,
        metadata: {
          ...next.metadata,
          updatedAt: new Date().toISOString(),
        },
      };
    });
  }).catch(error => {
    console.warn("Failed to refresh boolean text operand", error);
  });
}
